import { DocumentMapping } from "@/configuration/document-mappings/types";
import { Classification } from "@/src/models/constants/classifications";

export const TRICITY_MAPPINGS: Partial<Record<Classification, DocumentMapping>> = {
  [Classification.ADMIN_DISABILITY_FORMS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: {{ disabilityDurationType }} Disability Form - Action Required",
      "Task Note: {{ disabilityDurationType }} disability form. Please leave appropriate notes, collect fee, and reassign task back to appropriate clinic. Please document every aspect of this process.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Disability Form",
    documentLabel: "Disability Form {{ disabilityDurationType }} {{ signatureRequired }}",
    folder: "Patient Correspondence",
    folderMapping: "Patient Correspondence",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.ADMIN_DISABILITY_FORMS,
    group: "tco",
  },
  [Classification.ADMIN_FMLA_FORMS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: FMLA - Action Required",
      "Task Note: Please leave appropriate notes, collect fee, and reassign task back to appropriate clinic. Please document every aspect of this process.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "FMLA Form",
    documentLabel: "FMLA Form {{ signatureRequired }}",
    folder: "Patient Correspondence",
    folderMapping: "Patient Correspondence",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.ADMIN_FMLA_FORMS,
    group: "tco",
  },
  [Classification.ADMIN_OTHER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: Admin Doc - Action Required",
      "Task Note: Please leave appropriate notes, collect any appropriate fees, and reassign task back to appropriate clinic. Please document every aspect of this process.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Admin Document",
    documentLabel: "Admin Document {{ signatureRequired }}",
    folder: "Patient Correspondence",
    folderMapping: "Patient Correspondence",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.ADMIN_OTHER,
    group: "tco",
  },
  [Classification.ADMIN_PFML_FORMS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: {{ usStateName }} PFML - Action Required",
      "Task Note: {{ usStateName }} PFML form. Please leave appropriate notes and reassign task back to appropriate clinic. Please document every aspect of this process.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "PFML Form",
    documentLabel: "{{ usStateName }} PFML Form {{ signatureRequired }}",
    folder: "Patient Correspondence",
    folderMapping: "Patient Correspondence",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.ADMIN_PFML_FORMS,
    group: "tco",
  },
  [Classification.ADMIN_WORKERS_COMP]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: L&I {{ documentType }}",
      "Task Note: L&I {{ documentType }} rcvd. No signature required. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "TCO Front Desk - Clinic",
    displayName: "L&I Document",
    documentLabel: "L&I {{ documentType }}",
    folder: "LNI",
    folderMapping: "LNI",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.ADMIN_WORKERS_COMP,
    group: "tco",
  },
  [Classification.CLEARANCE_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: Clearance Request",
      "Task Note: Clearance request received. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Clearance Request",
    documentLabel: "{{ companyName }} {{ specialty }} Clearance Request {{ signatureRequired }}",
    folder: "Medical Records",
    folderMapping: "Medical Records",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.CLEARANCE_REQUEST,
    group: "tco",
  },
  [Classification.CLEARANCE_REQUEST_RESPONSE_OTHER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Clearance Response",
      "Task Note: Clearance response received. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    secondaryAssignedTo: "ASC Preadmissions",
    displayName: "Clearance Response",
    documentLabel: "{{ companyName }} {{ specialty }} Clearance Response {{ authorizationStatus }}",
    folder: "Pre-Op",
    folderMapping: "Pre-Op",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.CLEARANCE_REQUEST_RESPONSE_OTHER,
    group: "tco",
  },
  [Classification.CLEARANCE_REQUEST_RESPONSE_PRE_OP]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Pre-Op Clearance Response",
      "Task Note: Pre-op clearance response received. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    secondaryAssignedTo: "ASC Preadmissions",
    displayName: "Pre-Op Clearance Response",
    documentLabel: "{{ companyName }} {{ specialty }} Sx Clearance Response {{ authorizationStatus deniedOnly }}",
    folder: "Pre-Op",
    folderMapping: "Pre-Op",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.CLEARANCE_REQUEST_RESPONSE_PRE_OP,
    group: "tco",
  },
  [Classification.DME_ORDER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: DME - Action Required",
      "Task Note: DME-related doc rcvd. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "DME",
    documentLabel: "DME {{ signatureRequired }}",
    folder: "Orders & Rx",
    folderMapping: "Orders & Rx",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.DME_ORDER,
    group: "tco",
  },
  [Classification.FINANCIAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Fax Rcvd: Financial",
      "Task Note: Please review and follow-up as needed.",
    ],
    assignedTo: "Billing Staff Group",
    displayName: "Financial Document",
    documentLabel: "Financial Document",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.FINANCIAL,
    group: "tco",
  },
  [Classification.FINANCIAL_LEGAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: Legal Request for Financial Info",
      "Task Note: Rcvd request for financial info. Please review and respond as needed or reassign to appropriate staff member(s).",
    ],
    assignedTo: "Billing Staff Group",
    displayName: "Financial Legal Request",
    documentLabel: "Financial Document",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.FINANCIAL_LEGAL,
    group: "tco",
  },
  [Classification.IMAGING_RESULTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: [External] {{ imagingTypes }} Results - {{ bodyPartLaterality }}{{ bodyParts }}",
      "Task Note: Rcvd external imaging results. Please review and follow up with appropriate clinical staff as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "{{ imagingTypes }} Results",
    documentLabel: "{{ imagingTypes }} {{ bodyPartLaterality }}{{ bodyParts }}",
    folder: "Results - Radiology",
    folderMapping: "Results - Radiology",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.IMAGING_RESULTS,
    group: "tco",
  },
  [Classification.INSURANCE_COMMUNICATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: Insurance Correspondence - Action Required",
      "Task Note: Insurance correspondence received — not claims-related. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Billing Staff Group",
    displayName: "Misc Insurance Communication",
    documentLabel: "Misc Insurance Communication {{ signatureRequired }}",
    folder: "Insurance Correspondence",
    folderMapping: "Insurance Correspondence",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.INSURANCE_COMMUNICATION,
    group: "tco",
  },
  [Classification.JUNK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Junk",
    documentLabel: "",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    classification: Classification.JUNK,
    group: "tco",
  },
  [Classification.LAB_RESULTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Lab Results",
      "Task Note: Rcvd external lab results, completed on {{ dateOfEvent MM/DD/YYYY }}. Please review and follow up with appropriate clinical staff as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded MM/DD/YYYY }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Lab Results",
    documentLabel: "Lab Results",
    folder: "Labs",
    folderMapping: "Labs",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    priority: "High",
    classification: Classification.LAB_RESULTS,
    group: "tco",
  },
  [Classification.LEGAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Legal Doc",
      "Task Note: Please review and forward to appropriate attorney or internal staff member(s).\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Attorney",
    displayName: "Legal Document",
    documentLabel: "Legal Document {{ signatureRequired }}",
    folder: "Attorney",
    folderMapping: "Attorney",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.LEGAL,
    group: "tco",
  },
  [Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Fax Rcvd: Chart from Clinic",
      "Task Note: Rcvd patient chart. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Chart from Clinic",
    documentLabel: "{{ clinicName }} Chart from Clinic",
    folder: "External Clinical Note",
    folderMapping: "External Clinical Note",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC,
    group: "tco",
  },
  [Classification.MEDICAL_RECORD_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: Medical Records Request",
      "Task Note: Medical record request received. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Medical Records - VRC",
    displayName: "Medical Record Request",
    documentLabel: "Med Records Req",
    folder: "PHI Release",
    folderMapping: "PHI Release",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.MEDICAL_RECORD_REQUEST,
    group: "tco",
  },
  [Classification.MEDICAL_RECORD_REQUEST_IMAGING_CD]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: Medical Records Request - STAT CD",
      "Task Note: STAT imaging record request. Burn the requested imaging to CD and send. Please Include: Body Part, Specify MRI, X-ray or CT, and Date Range.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Medical Records - TCO STAT CD - PACS Images Only",
    displayName: "Medical Record Request - Imaging CD",
    documentLabel: "Med Records Req",
    folder: "PHI Release",
    folderMapping: "PHI Release",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    priority: "STAT",
    classification: Classification.MEDICAL_RECORD_REQUEST_IMAGING_CD,
    group: "tco",
  },
  [Classification.MEDICAL_RECORD_REQUEST_LEGAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: Subpoena for Records - Action Required",
      "Task Note: Subpoena for medical records received. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Valerie Health Catchall",
    displayName: "Subpoena for Records",
    documentLabel: "Subpoena for Records {{ signatureRequired }}",
    folder: "Attorney",
    folderMapping: "Attorney",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    priority: "High",
    classification: Classification.MEDICAL_RECORD_REQUEST_LEGAL,
    group: "tco",
  },
  [Classification.MEDICAL_RECORD_REQUEST_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Medical Records Response",
      "Task Note: Rcvd response to medical record request. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Valerie Health Catchall",
    displayName: "Medical Records Response",
    documentLabel: "{{ companyName }} Response to Medical Records Request",
    folder: "Medical Records",
    folderMapping: "Medical Records",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.MEDICAL_RECORD_REQUEST_RESPONSE,
    group: "tco",
  },
  [Classification.NOTIFICATION_APPOINTMENT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Appointment Notification",
    documentLabel: "{{ companyName }} Scheduling Notification",
    folder: "External Clinical Note",
    folderMapping: "External Clinical Note",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.NOTIFICATION_APPOINTMENT,
    group: "tco",
  },
  [Classification.NOTIFICATION_HOSPITAL_ADMIT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Hospital Admit Notice",
      "Task Note: Rcvd admit notice. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Hospital Admit Notification",
    documentLabel: "{{ clinicName }} Admit Notification",
    folder: "Medical Records",
    folderMapping: "Medical Records",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.NOTIFICATION_HOSPITAL_ADMIT,
    group: "tco",
  },
  [Classification.NOTIFICATION_HOSPITAL_DISCHARGE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Hospital Discharge Notice",
      "Task Note: Rcvd discharge notice. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Hospital Discharge Notification",
    documentLabel: "{{ clinicName }} Discharge Notification",
    folder: "External Visit Note",
    folderMapping: "External Visit Note",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.NOTIFICATION_HOSPITAL_DISCHARGE,
    group: "tco",
  },
  [Classification.OTHER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Miscellaneous Doc",
      "Task Note: Miscellaneous document processed by Valerie. Please review and follow up as needed. Escalate to Valerie if a standard workflow for this type of document is needed.",
    ],
    assignedTo: "Valerie Health Catchall",
    displayName: "Other",
    documentLabel: "Other",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.OTHER,
    group: "tco",
  },
  [Classification.PHARMACY_RELATED]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: Rx Related Doc - Action Required",
      "Task Note: Rx related doc rcvd from {{ companyName }}; action required. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Rx Related Document",
    documentLabel: "Pharmacy Related",
    folder: "Prescriptions & Renewals",
    folderMapping: "Prescriptions & Renewals",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PHARMACY_RELATED,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Authorization",
      "Task Details: Prior Auth Request",
      "Task Note: PA request rcvd. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Faxes-Authorization",
    displayName: "Prior Authorization",
    documentLabel: "PA Request",
    folder: "Pre-Certification",
    folderMapping: "Pre-Certification",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_IMAGING_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Authorization",
      "Task Details: Imaging Prior Auth Request - {{ imagingType }} {{ bodyPartLaterality }}{{ bodyPart }}",
      "Task Note: Imaging PA request rcvd. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Faxes-MRI",
    displayName: "Imaging PA Request",
    documentLabel: "{{ imagingType }} PA Request {{ bodyPartLaterality }}{{ bodyPart }}",
    folder: "Insurance Correspondence",
    folderMapping: "Insurance Correspondence",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_IMAGING_REQUEST,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Imaging Prior Auth Response - {{ authorizationStatus }}",
      "Task Note: Imaging PA response received. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Faxes-MRI",
    displayName: "Imaging PA Response",
    documentLabel: "{{ imagingType }} PA {{ authorizationStatus }} {{ bodyPartLaterality }}{{ bodyPart }}",
    folder: "Pre-Certification",
    folderMapping: "Pre-Certification",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Authorization",
      "Task Details: Medication Prior Auth Request",
      "Task Note: Medication PA request rcvd. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Medication PA Request",
    documentLabel: "Medication PA Request",
    folder: "Insurance Correspondence",
    folderMapping: "Insurance Correspondence",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Medication Prior Auth Response - {{ authorizationStatus }}",
      "Task Note: Medication PA response received. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Medication PA Response",
    documentLabel: "Medication PA Response {{ authorizationStatus }}",
    folder: "Pre-Certification",
    folderMapping: "Pre-Certification",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Authorization",
      "Task Details: Procedure Prior Auth Request",
      "Task Note: Procedure PA request rcvd. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Faxes-Authorization",
    displayName: "Procedure PA Request",
    documentLabel: "Procedure PA Request",
    folder: "Insurance Correspondence",
    folderMapping: "Insurance Correspondence",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Procedure Prior Auth Response - {{ authorizationStatus }}",
      "Task Note: Procedure PA response received. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Faxes-Authorization",
    displayName: "Procedure PA Response",
    documentLabel: "Procedure PA {{ authorizationStatus }}",
    folder: "Pre-Certification",
    folderMapping: "Pre-Certification",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_REFERRAL_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Authorization",
      "Task Details: Referral Prior Auth Request",
      "Task Note: PA request rcvd. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Referral PA Request",
    documentLabel: "Referral PA Request",
    folder: "Insurance Correspondence",
    folderMapping: "Insurance Correspondence",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_REFERRAL_REQUEST,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_REFERRAL_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Referral Prior Auth Response - {{ authorizationStatus }}",
      "Task Note: PA response received. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Faxes-Authorization",
    displayName: "Referral PA Response",
    documentLabel: "Referral PA {{ authorizationStatus }}",
    folder: "Pre-Certification",
    folderMapping: "Pre-Certification",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_REFERRAL_RESPONSE,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_SURGERY_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Authorization",
      "Task Details: Surgery Prior Auth Request",
      "Task Note: Surgery PA request rcvd. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Faxes-Authorization",
    displayName: "Surgery PA Request",
    documentLabel: "Surgery PA Request",
    folder: "Insurance Correspondence",
    folderMapping: "Insurance Correspondence",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_SURGERY_REQUEST,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_SURGERY_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Surgery Prior Auth Response - {{ authorizationStatus }}",
      "Task Note: Surgery PA response received. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Faxes-Authorization",
    displayName: "Surgery PA Response",
    documentLabel: "Surgery PA {{ authorizationStatus }}",
    folder: "Pre-Certification",
    folderMapping: "Pre-Certification",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_SURGERY_RESPONSE,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_THERAPY_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Authorization",
      "Task Details: Therapy Prior Auth Request",
      "Task Note: Therapy PA request rcvd. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Faxes-Therapy",
    displayName: "Therapy PA Request",
    documentLabel: "Therapy PA Request",
    folder: "Insurance Correspondence",
    folderMapping: "Insurance Correspondence",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_THERAPY_REQUEST,
    group: "tco",
  },
  [Classification.PRIOR_AUTHORIZATION_THERAPY_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Therapy Prior Auth Response - {{ authorizationStatus }}",
      "Task Note: Therapy PA response received. Please review and follow up as needed.\n\nIf this document is not for you and you do not know who it belongs to, please reassign this task to the front desk.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Faxes-Therapy",
    displayName: "Therapy PA Response",
    documentLabel: "Therapy PA {{ authorizationStatus }}",
    folder: "Pre-Certification",
    folderMapping: "Pre-Certification",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PRIOR_AUTHORIZATION_THERAPY_RESPONSE,
    group: "tco",
  },
  [Classification.PROGRESS_NOTE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: External {{ specialty }} CN",
      "Task Note: Rcvd external {{ specialty }} CN. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Progress Note",
    documentLabel: "{{ companyName }} {{ specialty }} CN {{ bodyPartLaterality }}{{ bodyPart }}",
    folder: "External Clinical Note",
    folderMapping: "External Clinical Note",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PROGRESS_NOTE,
    group: "tco",
  },
  [Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Hospital Discharge Summary",
      "Task Note: Rcvd hospital discharge summary. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Hospital Discharge Summary",
    documentLabel: "{{ companyName }} Discharge Summary {{ bodyPartLaterality }}{{ bodyPart }}",
    folder: "External Clinical Note",
    folderMapping: "External Clinical Note",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY,
    group: "tco",
  },
  [Classification.PROGRESS_NOTE_HOSPITAL_SUMMARY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Hospital Summary",
      "Task Note: Rcvd hospital summary. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Hospital Summary",
    documentLabel: "{{ companyName }} Hospital Summary {{ bodyPartLaterality }}{{ bodyPart }}",
    folder: "External Clinical Note",
    folderMapping: "External Clinical Note",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PROGRESS_NOTE_HOSPITAL_SUMMARY,
    group: "tco",
  },
  [Classification.PROGRESS_NOTE_OPERATIVE_REPORT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Operative Report",
    documentLabel: "{{ companyName }} Operative Report {{ bodyPartLaterality }}{{ bodyPart }}",
    folder: "Op Notes",
    folderMapping: "Op Notes",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
    group: "tco",
  },
  [Classification.PROGRESS_NOTE_PRE_OPERATIVE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: External Pre-Op Progress Note",
      "Task Note: Rcvd external pre-op progress note. Please review and follow up as needed.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Pre-Op Note",
    documentLabel: "{{ companyName }} Pre-Op Note {{ bodyPartLaterality }}{{ bodyPart }}",
    folder: "External Clinical Note",
    folderMapping: "External Clinical Note",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PROGRESS_NOTE_PRE_OPERATIVE,
    group: "tco",
  },
  [Classification.PROGRESS_NOTE_PT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: External PT/OT {{ documentType }} - Action Required",
      "Task Note: Rcvd external pt/ot {{ documentType }}; response requested. Please review and follow up as needed.\n\nDocument uploaded on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "PT/OT Progress Note",
    documentLabel: "{{ companyName }} PT {{ documentType }} {{ bodyPartLaterality }}{{ bodyPart }}",
    folder: "External Clinical Note",
    folderMapping: "External Clinical Note",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.PROGRESS_NOTE_PT,
    group: "tco",
  },
  [Classification.REFERRAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Refer",
      "Task Details: New Referral",
      "Task Note: New patient referral processed by Valerie. Note composed by the inbound referral workflow (lead + eligibility + scheduling).",
    ],
    assignedTo: "Referral cascade",
    displayName: "Referral",
    documentLabel: "Referral {{ reasonForReferral }}",
    folder: "Medical Records",
    folderMapping: "Medical Records",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Inbound Referral WF",
    priority: "High",
    classification: Classification.REFERRAL,
    group: "tco",
  },
  [Classification.REFERRAL_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Review",
      "Task Details: Referral Response",
      "Task Note: Referral response received. Please review and follow up accordingly.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Nurse for fax addressee",
    displayName: "Response to Referral",
    documentLabel: "Response to Referral {{ signatureRequired }}",
    folder: "External Clinical Note",
    folderMapping: "External Clinical Note",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.REFERRAL_RESPONSE,
    group: "tco",
  },
  [Classification.REFERRAL_STATUS_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: Communication",
      "Task Details: Referral Status Request",
      "Task Note: Requesting referral status details. Please review and follow up accordingly.\n\nDocument uploaded to patient chart on {{ dateUploaded }}.",
    ],
    assignedTo: "Valerie Health Catchall",
    displayName: "Referral Status Request",
    documentLabel: "Referral Status Request",
    folder: "Medical Records",
    folderMapping: "Medical Records",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Upload + Task",
    classification: Classification.REFERRAL_STATUS_REQUEST,
    group: "tco",
  },
} as const;
